import { useState, useEffect, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';

// material-ui
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useTheme } from '@mui/material/styles';
import { format, subDays, startOfDay } from 'date-fns';

// ant design icons
import DisconnectOutlined from '@ant-design/icons/DisconnectOutlined';
import PlusOutlined from '@ant-design/icons/PlusOutlined';
import DeleteOutlined from '@ant-design/icons/DeleteOutlined';
import EditOutlined from '@ant-design/icons/EditOutlined';
import SaveOutlined from '@ant-design/icons/SaveOutlined';
import CheckOutlined from '@ant-design/icons/CheckOutlined';
import ClockCircleOutlined from '@ant-design/icons/ClockCircleOutlined';
import FileSearchOutlined from '@ant-design/icons/FileSearchOutlined';

// project imports
import MainCard from 'components/MainCard';
import HistoryPagination, { HistoryEmptyState } from 'components/HistoryPagination';
import useDatasheetHistory, { HISTORY_PAGE_SIZE, historyDateKey } from 'hooks/useDatasheetHistory';
import ABCHistoricalTable from './ABCHistoricalTable';
import { openSnackbar } from 'api/snackbar';
import useOnlineStatus from 'hooks/useOnlineStatus';

// ant design icons for offline
import LeftOutlined from '@ant-design/icons/LeftOutlined';
import SpeechTextField from 'components/SpeechTextField';

const ABCMode = ({
  viewMode = 'individual',
  onDataChange,
  // Shared ABC records state (from parent DataCollection)
  abcRecords: externalRecords,
  onRecordsChange,
  // Offline mode props
  isOfflineMode = false,
  childId,
  childName,
  therapistId,
  therapistName,
  sessionId,
  onSave,
  onBack
}) => {
  const theme = useTheme();
  const intl = useIntl();

  // Get actual network status - auto-detect offline regardless of isOfflineMode prop
  const { isOnline } = useOnlineStatus();

  const commonSettings = [
    intl.formatMessage({ id: 'form.abc.setting.classroom' }),
    intl.formatMessage({ id: 'form.abc.setting.playground' }),
    intl.formatMessage({ id: 'form.abc.setting.cafeteria' }),
    intl.formatMessage({ id: 'form.abc.setting.hallway' }),
    intl.formatMessage({ id: 'form.abc.setting.therapyRoom' }),
    intl.formatMessage({ id: 'form.abc.setting.outdoorArea' }),
    intl.formatMessage({ id: 'form.abc.setting.home' }),
    intl.formatMessage({ id: 'form.abc.setting.community' })
  ];

  const commonAntecedents = [
    intl.formatMessage({ id: 'form.abc.antecedent.deniedAccess' }),
    intl.formatMessage({ id: 'form.abc.antecedent.difficultTask' }),
    intl.formatMessage({ id: 'form.abc.antecedent.transition' }),
    intl.formatMessage({ id: 'form.abc.antecedent.askedToWait' }),
    intl.formatMessage({ id: 'form.abc.antecedent.peerInteraction' }),
    intl.formatMessage({ id: 'form.abc.antecedent.changeInRoutine' }),
    intl.formatMessage({ id: 'form.abc.antecedent.loudNoise' }),
    intl.formatMessage({ id: 'form.abc.antecedent.toldNo' }),
    intl.formatMessage({ id: 'form.abc.antecedent.endPreferredActivity' }),
    intl.formatMessage({ id: 'common.other' })
  ];

  const commonBehaviors = [
    intl.formatMessage({ id: 'form.abc.behavior.tantrum' }),
    intl.formatMessage({ id: 'form.abc.behavior.aggression' }),
    intl.formatMessage({ id: 'form.abc.behavior.selfInjury' }),
    intl.formatMessage({ id: 'form.abc.behavior.propertyDestruction' }),
    intl.formatMessage({ id: 'form.abc.behavior.elopement' }),
    intl.formatMessage({ id: 'form.abc.behavior.nonCompliance' }),
    intl.formatMessage({ id: 'form.abc.behavior.stereotypy' }),
    intl.formatMessage({ id: 'common.other' })
  ];

  const commonConsequences = [
    intl.formatMessage({ id: 'form.abc.consequence.removedFromSituation' }),
    intl.formatMessage({ id: 'form.abc.consequence.givenItemActivity' }),
    intl.formatMessage({ id: 'form.abc.consequence.redirected' }),
    intl.formatMessage({ id: 'form.abc.consequence.ignored' }),
    intl.formatMessage({ id: 'form.abc.consequence.physicalPrompt' }),
    intl.formatMessage({ id: 'form.abc.consequence.verbalReprimand' }),
    intl.formatMessage({ id: 'form.abc.consequence.timeInSafeSpace' }),
    intl.formatMessage({ id: 'common.other' })
  ];

  const [showAddForm, setShowAddForm] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [startDate, setStartDate] = useState(subDays(new Date(), 7));
  const [endDate, setEndDate] = useState(new Date());
  const [tabValue, setTabValue] = useState(0);
  const [sessionNotes, setSessionNotes] = useState('');
  const [saving, setSaving] = useState(false);

  // Ref to skip auto-save on initial render
  const initialRenderRef = useRef(true);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Unified data source for both individual and grid modes
  // Use external records if provided, otherwise use local state
  const [localRecords, setLocalRecords] = useState([]);

  /* PREVIOUSLY SAVED INCIDENTS. The parent hands down `abcRecords`, but only ever
   * initialises it to [] — nothing read the stored entries back, so the Historical
   * tab was blank on open. Each saved entry holds a session's worth of incidents
   * under raw_data.records, flattened here into the row shape this sheet renders. */
  const {
    records: historyRecords,
    page: historyPage,
    totalPages: historyTotalPages,
    total: historyTotal,
    setPage: setHistoryPage,
    loading: historyLoading,
    isEmpty: historyIsEmpty
  } = useDatasheetHistory(childId, 'abc');

  const savedRecords = useMemo(
    () =>
      historyRecords.flatMap((record) => {
        const key = historyDateKey(record);
        const rows = record.raw_data?.records;
        if (!key || !Array.isArray(rows)) return [];
        const [y, m, d] = key.split('-').map(Number);
        if (!y || !m || !d) return [];
        const day = new Date(y, m - 1, d);
        return rows.map((r, i) => ({
          id: `saved-${record.id}-${i}`,
          timestamp: day,
          date: day,
          time: r.time || '',
          setting: r.setting || '',
          antecedent: r.antecedent || '',
          behavior: r.behavior || '',
          consequence: r.consequence || '',
          duration: r.duration || '',
          notes: r.notes || '',
          isSaved: true
        }));
      }),
    [historyRecords]
  );

  // If external records are provided, use them; otherwise use local
  const liveRecords = externalRecords && externalRecords.length > 0 ? externalRecords : localRecords;

  /* Live rows first, then saved ones, newest first. A saved row matching one already
   * on screen is dropped — after today's session is submitted the fetch would
   * otherwise return it alongside the copy still held in memory. */
  const allRecords = useMemo(() => {
    const signature = (r) => `${startOfDay(r.date || r.timestamp).getTime()}|${r.time}|${r.behavior}|${r.antecedent}`;
    const seen = new Set(liveRecords.map(signature));
    return [...liveRecords, ...savedRecords.filter((r) => !seen.has(signature(r)))].sort(
      (a, b) => new Date(b.timestamp) - new Date(a.timestamp)
    );
  }, [liveRecords, savedRecords]);

  // Wrapper to update both local and external state
  const setAllRecords = (newRecordsOrUpdater) => {
    const newRecords = typeof newRecordsOrUpdater === 'function' ? newRecordsOrUpdater(allRecords) : newRecordsOrUpdater;

    setLocalRecords(newRecords);
    if (onRecordsChange) {
      onRecordsChange(newRecords);
    }
  };

  // Sync local records to parent on initial load (only if there are records)
  useEffect(() => {
    if (onRecordsChange && localRecords.length > 0 && (!externalRecords || externalRecords.length === 0)) {
      onRecordsChange(localRecords);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-save: notify parent when form data changes for debounced save to IndexedDB → PostgreSQL
  useEffect(() => {
    if (initialRenderRef.current) {
      initialRenderRef.current = false;
      return;
    }

    // Skip if no data has been entered
    const todayStart = startOfDay(new Date());
    const currentTodaysRecords = allRecords.filter((record) => {
      const recordDate = startOfDay(record.date);
      return recordDate.getTime() === todayStart.getTime();
    });

    if (currentTodaysRecords.length === 0 && !sessionNotes) {
      return;
    }

    const formData = {
      records: currentTodaysRecords.map((record) => ({
        time: record.time,
        setting: record.setting,
        antecedent: record.antecedent,
        behavior: record.behavior,
        consequence: record.consequence,
        duration: record.duration,
        notes: record.notes
      })),
      totalRecords: currentTodaysRecords.length,
      sessionNotes
    };

    if (onDataChange) onDataChange('abc', formData);
  }, [allRecords, sessionNotes]); // eslint-disable-line react-hooks/exhaustive-deps

  // Filter today's records for individual mode
  const todaysRecords = allRecords.filter((record) => {
    const recordDate = startOfDay(record.date);
    const today = startOfDay(new Date());
    return recordDate.getTime() === today.getTime();
  });

  const [newRecord, setNewRecord] = useState({
    date: new Date(),
    time: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
    setting: '',
    antecedent: '',
    behavior: '',
    consequence: '',
    duration: '',
    notes: ''
  });

  const handleAddRecord = () => {
    if (!newRecord.antecedent || !newRecord.behavior || !newRecord.consequence) {
      return;
    }

    if (editingRecord) {
      // Update existing record
      setAllRecords(allRecords.map((r) => (r.id === editingRecord.id ? { ...editingRecord, ...newRecord, timestamp: newRecord.date } : r)));
      setEditingRecord(null);
    } else {
      // Add new record
      const record = {
        id: Date.now(),
        timestamp: newRecord.date || new Date(),
        date: newRecord.date || new Date(),
        ...newRecord
      };
      setAllRecords([record, ...allRecords].sort((a, b) => b.timestamp - a.timestamp));
    }

    setNewRecord({
      date: new Date(),
      time: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
      setting: '',
      antecedent: '',
      behavior: '',
      consequence: '',
      duration: '',
      notes: ''
    });
    setShowAddForm(false);

    // Auto-save handled by useEffect on state changes → onDataChange → parent's persistToIndexedDB (upsert)
  };

  const handleEditRecord = (record) => {
    setEditingRecord(record);
    setNewRecord({
      date: record.date || new Date(),
      time: record.time,
      setting: record.setting || '',
      antecedent: record.antecedent,
      behavior: record.behavior,
      consequence: record.consequence,
      duration: record.duration || '',
      notes: record.notes || ''
    });
    setShowAddForm(true);
  };

  const handleDeleteRecord = (id) => {
    setAllRecords(allRecords.filter((r) => r.id !== id));

    // Auto-save handled by useEffect on state changes → onDataChange → parent's persistToIndexedDB (upsert)
  };

  const handleCancelForm = () => {
    setShowAddForm(false);
    setEditingRecord(null);
    setNewRecord({
      date: new Date(),
      time: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
      setting: '',
      antecedent: '',
      behavior: '',
      consequence: '',
      duration: '',
      notes: ''
    });
  };

  // Handle save - uses offline-first pattern (always saves to IndexedDB, syncs in background)
  const handleSave = async (showNotification = true) => {
    setSaving(true);
    try {
      // Don't save if no data
      if (todaysRecords.length === 0) {
        setSaving(false);
        return;
      }

      const formData = {
        records: todaysRecords.map((record) => ({
          time: record.time,
          setting: record.setting,
          antecedent: record.antecedent,
          behavior: record.behavior,
          consequence: record.consequence,
          duration: record.duration,
          notes: record.notes
        })),
        totalRecords: todaysRecords.length,
        sessionNotes
      };

      // Notify parent to persist via its upsert pattern (persistToIndexedDB)
      if (onDataChange) {
        onDataChange('abc', formData);
      }

      if (showNotification) {
        openSnackbar({
          open: true,
          message: isOnline
            ? intl.formatMessage({ id: 'form.msg.dataSaved' }, { formName: 'ABC' })
            : intl.formatMessage({ id: 'form.msg.dataSavedOffline' }, { formName: 'ABC' }),
          variant: 'alert',
          alert: { color: 'success' }
        });
      }

      if (onSave) onSave();
      if (isOfflineMode && onBack) onBack();
    } catch (error) {
      console.error('Error saving entry:', error);
      if (showNotification) {
        openSnackbar({
          open: true,
          message: intl.formatMessage({ id: 'form.msg.saveFailed' }),
          variant: 'alert',
          alert: { color: 'error' }
        });
      }
    } finally {
      setSaving(false);
    }
  };

  // Alias for backward compatibility with existing UI buttons
  const handleOfflineSave = handleSave;

  // Individual View - Form for adding ABC records
  if (viewMode === 'individual') {
    return (
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Box sx={isOfflineMode ? { p: { xs: 2, sm: 3 } } : {}}>
          {/* Network Lost Banner - shown when not in explicit offline mode but network is down */}
          {!isOfflineMode && !isOnline && (
            <Alert severity="info" icon={<DisconnectOutlined />} sx={{ mb: 2 }}>
              <Typography variant="body2">{intl.formatMessage({ id: 'form.msg.offlineNote' })}</Typography>
            </Alert>
          )}

          {/* Offline Mode Header */}
          {isOfflineMode && (
            <MainCard sx={{ mb: 3 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Button variant="text" startIcon={<LeftOutlined />} onClick={onBack}>
                    {intl.formatMessage({ id: 'form.button.back' })}
                  </Button>
                  <Box>
                    <Typography variant="h5">{intl.formatMessage({ id: 'form.abc.headerTitle' }, { childName })}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {intl.formatMessage({ id: 'form.label.offlineMode' })}
                    </Typography>
                  </Box>
                </Stack>
                <Button
                  variant="contained"
                  startIcon={<SaveOutlined />}
                  onClick={handleOfflineSave}
                  disabled={saving || todaysRecords.length === 0}
                >
                  {saving ? intl.formatMessage({ id: 'form.button.saving' }) : intl.formatMessage({ id: 'form.button.saveOffline' })}
                </Button>
              </Stack>
            </MainCard>
          )}

          <Stack spacing={3}>
            {/* Today's ABC Records with Add Form */}
            <MainCard>
              <Stack spacing={2}>
                {/* Header with New Record Button */}
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="h5">
                    {intl.formatMessage({ id: 'form.abc.todaysRecords' }, { count: todaysRecords.length })}
                  </Typography>
                  {!showAddForm && (
                    <Button variant="contained" startIcon={<PlusOutlined />} onClick={() => setShowAddForm(true)}>
                      {intl.formatMessage({ id: 'form.abc.new' })}
                    </Button>
                  )}
                </Stack>

                {/* Add/Edit Form */}
                {showAddForm && (
                  <Paper sx={{ p: 3, bgcolor: 'grey.50', border: 1, borderColor: 'divider' }}>
                    <Stack spacing={3}>
                      {/* Date and Time Row */}
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                        <DatePicker
                          label={intl.formatMessage({ id: 'form.label.date' })}
                          value={newRecord.date}
                          onChange={(newValue) => {
                            setNewRecord({ ...newRecord, date: newValue });
                            if (onDataChange) {
                              onDataChange();
                            }
                          }}
                          slotProps={{ textField: { fullWidth: true } }}
                        />
                        <TextField
                          fullWidth
                          label={intl.formatMessage({ id: 'form.label.time' })}
                          value={newRecord.time}
                          onChange={(e) => {
                            setNewRecord({ ...newRecord, time: e.target.value });
                            if (onDataChange) {
                              onDataChange();
                            }
                          }}
                          InputProps={{
                            startAdornment: <ClockCircleOutlined style={{ marginRight: 8, color: theme.palette.text.secondary }} />
                          }}
                        />
                      </Stack>

                      {/* Setting */}
                      <Autocomplete
                        freeSolo
                        options={commonSettings}
                        value={newRecord.setting}
                        onChange={(event, newValue) => {
                          setNewRecord({ ...newRecord, setting: newValue || '' });
                          if (onDataChange) {
                            onDataChange();
                          }
                        }}
                        onInputChange={(event, newInputValue) => {
                          setNewRecord({ ...newRecord, setting: newInputValue });
                          if (onDataChange) {
                            onDataChange();
                          }
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={intl.formatMessage({ id: 'form.abc.settingLabel' })}
                            placeholder={intl.formatMessage({ id: 'form.abc.settingPlaceholder' })}
                          />
                        )}
                      />

                      {/* Antecedent */}
                      <Box>
                        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1, color: 'primary.main' }}>
                          {intl.formatMessage({ id: 'form.abc.antecedentHeader' })}
                        </Typography>
                        <Autocomplete
                          freeSolo
                          options={commonAntecedents}
                          value={newRecord.antecedent}
                          onChange={(event, newValue) => {
                            setNewRecord({ ...newRecord, antecedent: newValue || '' });
                            if (onDataChange) {
                              onDataChange();
                            }
                          }}
                          onInputChange={(event, newInputValue) => {
                            setNewRecord({ ...newRecord, antecedent: newInputValue });
                            if (onDataChange) {
                              onDataChange();
                            }
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label={intl.formatMessage({ id: 'form.abc.selectAntecedent' })}
                              placeholder={intl.formatMessage({ id: 'form.abc.antecedentPlaceholder' })}
                            />
                          )}
                        />
                      </Box>

                      {/* Behavior */}
                      <Box>
                        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1, color: 'warning.main' }}>
                          {intl.formatMessage({ id: 'form.abc.behaviorHeader' })}
                        </Typography>
                        <Autocomplete
                          freeSolo
                          options={commonBehaviors}
                          value={newRecord.behavior}
                          onChange={(event, newValue) => {
                            setNewRecord({ ...newRecord, behavior: newValue || '' });
                            if (onDataChange) {
                              onDataChange();
                            }
                          }}
                          onInputChange={(event, newInputValue) => {
                            setNewRecord({ ...newRecord, behavior: newInputValue });
                            if (onDataChange) {
                              onDataChange();
                            }
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label={intl.formatMessage({ id: 'form.abc.selectBehavior' })}
                              placeholder={intl.formatMessage({ id: 'form.abc.behaviorPlaceholder' })}
                            />
                          )}
                        />
                        <TextField
                          fullWidth
                          label={intl.formatMessage({ id: 'form.abc.durationMinutes' })}
                          type="number"
                          value={newRecord.duration}
                          onChange={(e) => {
                            setNewRecord({ ...newRecord, duration: e.target.value });
                            if (onDataChange) {
                              onDataChange();
                            }
                          }}
                          sx={{ mt: 2 }}
                        />
                      </Box>

                      {/* Consequence */}
                      <Box>
                        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1, color: 'success.main' }}>
                          {intl.formatMessage({ id: 'form.abc.consequenceHeader' })}
                        </Typography>
                        <Autocomplete
                          freeSolo
                          options={commonConsequences}
                          value={newRecord.consequence}
                          onChange={(event, newValue) => {
                            setNewRecord({ ...newRecord, consequence: newValue || '' });
                            if (onDataChange) {
                              onDataChange();
                            }
                          }}
                          onInputChange={(event, newInputValue) => {
                            setNewRecord({ ...newRecord, consequence: newInputValue });
                            if (onDataChange) {
                              onDataChange();
                            }
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label={intl.formatMessage({ id: 'form.abc.selectConsequence' })}
                              placeholder={intl.formatMessage({ id: 'form.abc.consequencePlaceholder' })}
                            />
                          )}
                        />
                      </Box>

                      {/* Notes */}
                      <TextField
                        fullWidth
                        multiline
                        rows={3}
                        label={intl.formatMessage({ id: 'form.abc.notesLabel' })}
                        placeholder={intl.formatMessage({ id: 'form.abc.notesPlaceholder' })}
                        value={newRecord.notes}
                        onChange={(e) => {
                          setNewRecord({ ...newRecord, notes: e.target.value });
                          if (onDataChange) {
                            onDataChange();
                          }
                        }}
                      />

                      {/* Actions */}
                      <Stack direction="row" spacing={2} justifyContent="flex-end">
                        <Button variant="outlined" onClick={handleCancelForm}>
                          {intl.formatMessage({ id: 'common.cancel' })}
                        </Button>
                        <Button
                          variant="contained"
                          startIcon={<CheckOutlined />}
                          onClick={handleAddRecord}
                          disabled={!newRecord.antecedent || !newRecord.behavior || !newRecord.consequence}
                        >
                          {editingRecord ? intl.formatMessage({ id: 'form.abc.update' }) : intl.formatMessage({ id: 'form.abc.addRecord' })}
                        </Button>
                      </Stack>
                    </Stack>
                  </Paper>
                )}

                {/* Records List */}
                {!showAddForm && todaysRecords.length === 0 ? (
                  <Stack alignItems="center" spacing={2} sx={{ py: 6 }}>
                    <FileSearchOutlined style={{ fontSize: '3rem', color: theme.palette.grey[300] }} />
                    <Typography variant="h6" color="text.secondary">
                      {intl.formatMessage({ id: 'form.abc.noRecordsYet' })}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {intl.formatMessage({ id: 'form.abc.clickNewRecord' })}
                    </Typography>
                  </Stack>
                ) : !showAddForm ? (
                  <Stack spacing={2}>
                    {todaysRecords.map((record) => (
                      <Paper key={record.id} sx={{ p: 3, border: 1, borderColor: 'divider' }}>
                        <Stack spacing={2}>
                          {/* Header */}
                          <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                              <ClockCircleOutlined style={{ fontSize: '1.2rem', color: theme.palette.text.secondary }} />
                              <Typography variant="subtitle2" fontWeight={600}>
                                {record.date ? format(record.date, 'MMM dd, yyyy') : ''} - {record.time}
                              </Typography>
                              {record.setting && <Chip label={record.setting} size="small" color="primary" variant="outlined" />}
                              {record.duration && (
                                <Chip
                                  label={intl.formatMessage({ id: 'form.abc.min' }, { duration: record.duration })}
                                  size="small"
                                  variant="outlined"
                                />
                              )}
                            </Stack>
                            <Stack direction="row" spacing={1}>
                              <IconButton size="small" color="primary" onClick={() => handleEditRecord(record)}>
                                <EditOutlined />
                              </IconButton>
                              <IconButton size="small" color="error" onClick={() => handleDeleteRecord(record.id)}>
                                <DeleteOutlined />
                              </IconButton>
                            </Stack>
                          </Stack>

                          {/* ABC Content */}
                          <Grid container spacing={2}>
                            <Grid size={{ xs: 12, md: 4 }}>
                              <Paper sx={{ p: 2, bgcolor: 'primary.lighter', height: '100%' }}>
                                <Typography variant="caption" fontWeight={700} color="primary.main" sx={{ mb: 1, display: 'block' }}>
                                  {intl.formatMessage({ id: 'form.abc.antecedentSection' })}
                                </Typography>
                                <Typography variant="body2" fontWeight={600}>
                                  {record.antecedent}
                                </Typography>
                              </Paper>
                            </Grid>
                            <Grid size={{ xs: 12, md: 4 }}>
                              <Paper sx={{ p: 2, bgcolor: 'warning.lighter', height: '100%' }}>
                                <Typography variant="caption" fontWeight={700} color="warning.main" sx={{ mb: 1, display: 'block' }}>
                                  {intl.formatMessage({ id: 'form.abc.behaviorSection' })}
                                </Typography>
                                <Typography variant="body2" fontWeight={600}>
                                  {record.behavior}
                                </Typography>
                              </Paper>
                            </Grid>
                            <Grid size={{ xs: 12, md: 4 }}>
                              <Paper sx={{ p: 2, bgcolor: 'success.lighter', height: '100%' }}>
                                <Typography variant="caption" fontWeight={700} color="success.main" sx={{ mb: 1, display: 'block' }}>
                                  {intl.formatMessage({ id: 'form.abc.consequenceSection' })}
                                </Typography>
                                <Typography variant="body2" fontWeight={600}>
                                  {record.consequence}
                                </Typography>
                              </Paper>
                            </Grid>
                          </Grid>

                          {/* Notes */}
                          {record.notes && (
                            <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                              <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                                {intl.formatMessage({ id: 'form.abc.notesSection' })}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {record.notes}
                              </Typography>
                            </Paper>
                          )}
                        </Stack>
                      </Paper>
                    ))}
                  </Stack>
                ) : null}
              </Stack>
            </MainCard>

            {/* Session Notes - Offline Mode Only */}
            {isOfflineMode && (
              <MainCard>
                <Typography variant="subtitle1" sx={{ mb: 2 }}>
                  {intl.formatMessage({ id: 'form.label.sessionNotes' })}
                </Typography>
                <SpeechTextField
                  fullWidth
                  multiline
                  rows={3}
                  placeholder={intl.formatMessage({ id: 'form.label.notesPlaceholder' })}
                  value={sessionNotes}
                  onChange={(e) => setSessionNotes(e.target.value)}
                />
              </MainCard>
            )}
          </Stack>
        </Box>
      </LocalizationProvider>
    );
  }

  // Grid View - Table with all records
  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Stack spacing={3}>
        {/* Network Lost Banner - shown when not in explicit offline mode but network is down */}
        {!isOfflineMode && !isOnline && (
          <Alert severity="info" icon={<DisconnectOutlined />}>
            <Typography variant="body2">{intl.formatMessage({ id: 'form.msg.offlineNote' })}</Typography>
          </Alert>
        )}

        {/* Summary Statistics — 4 cards to match the layout used by Behavior
            Interval and Injury Report. */}
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <MainCard>
              <Stack spacing={0.5}>
                <Typography variant="h3" fontWeight={700} color="primary.main">
                  {allRecords.length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {intl.formatMessage({ id: 'form.abc.totalRecordsLabel' })}
                </Typography>
              </Stack>
            </MainCard>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <MainCard>
              <Stack spacing={0.5}>
                <Typography variant="h3" fontWeight={700} color="success.main">
                  {todaysRecords.length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {intl.formatMessage({ id: 'form.abc.todaysRecordsLabel' })}
                </Typography>
              </Stack>
            </MainCard>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <MainCard>
              <Stack spacing={0.5}>
                <Typography variant="h3" fontWeight={700} color="info.main">
                  {new Set(allRecords.map((r) => r.behavior).filter(Boolean)).size}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {intl.formatMessage({ id: 'form.abc.uniqueBehaviors', defaultMessage: 'Unique Behaviors' })}
                </Typography>
              </Stack>
            </MainCard>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <MainCard>
              <Stack spacing={0.5}>
                <Typography variant="h3" fontWeight={700} color="secondary.main">
                  {new Set(allRecords.map((r) => r.antecedent).filter(Boolean)).size}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {intl.formatMessage({ id: 'form.abc.uniqueAntecedents', defaultMessage: 'Unique Antecedents' })}
                </Typography>
              </Stack>
            </MainCard>
          </Grid>
        </Grid>

        {/* Tabs for Data Entry and Historical Data */}
        <MainCard>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Tabs value={tabValue} onChange={handleTabChange}>
              <Tab label={intl.formatMessage({ id: 'form.tab.dataEntry' })} />
              <Tab label={intl.formatMessage({ id: 'form.tab.historicalData' })} />
            </Tabs>
            <Stack direction="row" spacing={1}>
              {tabValue === 0 && (
                <Button
                  variant="contained"
                  startIcon={<PlusOutlined />}
                  // Open the existing Add/Edit dialog (below in JSX) so therapists
                  // fill the record in a focused form — same UX as Injury Report.
                  // The old behaviour inserted an empty row into the table,
                  // which was easy to leave half-filled.
                  onClick={() => {
                    setEditingRecord(null);
                    setNewRecord({
                      date: new Date(),
                      time: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
                      setting: '',
                      antecedent: '',
                      behavior: '',
                      consequence: '',
                      duration: '',
                      notes: ''
                    });
                    setShowAddForm(true);
                  }}
                  size="small"
                >
                  {intl.formatMessage({ id: 'form.abc.new' })}
                </Button>
              )}
            </Stack>
          </Stack>

          <Box sx={{ pt: 3 }}>
            {/* Tab 0: Data Entry - Today's Data with Add/Edit/Delete */}
            {tabValue === 0 && (
              <ABCHistoricalTable
                data={todaysRecords}
                setData={(updateFn) => {
                  setAllRecords((old) => {
                    const todayData = old.filter((record) => {
                      const recordDate = startOfDay(record.date);
                      const today = startOfDay(new Date());
                      return recordDate.getTime() === today.getTime();
                    });
                    const historicalData = old.filter((record) => {
                      const recordDate = startOfDay(record.date);
                      const today = startOfDay(new Date());
                      return recordDate.getTime() !== today.getTime();
                    });
                    const updated = typeof updateFn === 'function' ? updateFn(todayData) : updateFn;
                    return [...updated, ...historicalData].sort((a, b) => b.timestamp - a.timestamp);
                  });
                }}
                startDate={null}
                endDate={null}
                setStartDate={() => {}}
                setEndDate={() => {}}
                dataEntryMode={true}
              />
            )}

            {/* Tab 1: Historical Data - Read-only with Search/Filters */}
            {tabValue === 1 &&
              (historyIsEmpty && allRecords.length === 0 ? (
                <HistoryEmptyState />
              ) : (
                <>
                  <ABCHistoricalTable
                    data={allRecords}
                    setData={setAllRecords}
                    startDate={startDate}
                    endDate={endDate}
                    setStartDate={setStartDate}
                    setEndDate={setEndDate}
                    dataEntryMode={false}
                  />
                  <HistoryPagination
                    page={historyPage}
                    totalPages={historyTotalPages}
                    total={historyTotal}
                    pageSize={HISTORY_PAGE_SIZE}
                    onChange={setHistoryPage}
                    loading={historyLoading}
                  />
                </>
              ))}
          </Box>
        </MainCard>

        {/* Add/Edit Dialog */}
        <Dialog open={showAddForm} onClose={handleCancelForm} maxWidth="md" fullWidth>
          <DialogTitle>
            {editingRecord ? intl.formatMessage({ id: 'form.abc.editRecord' }) : intl.formatMessage({ id: 'form.abc.addAbcRecord' })}
          </DialogTitle>
          <DialogContent>
            <Stack spacing={3} sx={{ mt: 2 }}>
              {/* Time */}
              <TextField
                fullWidth
                label={intl.formatMessage({ id: 'form.label.time' })}
                value={newRecord.time}
                onChange={(e) => setNewRecord({ ...newRecord, time: e.target.value })}
                InputProps={{
                  startAdornment: <ClockCircleOutlined style={{ marginRight: 8, color: theme.palette.text.secondary }} />
                }}
              />

              {/* Setting — where the behavior happened. Mirrors the
                  Setting column in the table so therapists can populate it
                  from inside the focused form too. */}
              <FormControl fullWidth>
                <InputLabel>{intl.formatMessage({ id: 'form.label.setting', defaultMessage: 'Setting' })}</InputLabel>
                <Select
                  value={newRecord.setting || ''}
                  label={intl.formatMessage({ id: 'form.label.setting', defaultMessage: 'Setting' })}
                  onChange={(e) => setNewRecord({ ...newRecord, setting: e.target.value })}
                >
                  <MenuItem value="">
                    <em>{intl.formatMessage({ id: 'form.abc.selectSetting', defaultMessage: 'Select setting' })}</em>
                  </MenuItem>
                  {commonSettings.map((s) => (
                    <MenuItem key={s} value={s}>
                      {s}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Antecedent */}
              <Box>
                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1, color: 'primary.main' }}>
                  {intl.formatMessage({ id: 'form.abc.antecedentHeader' })}
                </Typography>
                <FormControl fullWidth sx={{ mb: 1 }}>
                  <InputLabel>{intl.formatMessage({ id: 'form.abc.selectAntecedent' })}</InputLabel>
                  <Select
                    value={newRecord.antecedent}
                    label={intl.formatMessage({ id: 'form.abc.selectAntecedent' })}
                    onChange={(e) => setNewRecord({ ...newRecord, antecedent: e.target.value })}
                  >
                    {commonAntecedents.map((ant) => (
                      <MenuItem key={ant} value={ant}>
                        {ant}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  placeholder={intl.formatMessage({ id: 'form.abc.antecedentDetailsPlaceholder' })}
                  value={newRecord.antecedentDetails}
                  onChange={(e) => setNewRecord({ ...newRecord, antecedentDetails: e.target.value })}
                />
              </Box>

              {/* Behavior */}
              <Box>
                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1, color: 'warning.main' }}>
                  {intl.formatMessage({ id: 'form.abc.behaviorHeader' })}
                </Typography>
                <FormControl fullWidth sx={{ mb: 1 }}>
                  <InputLabel>{intl.formatMessage({ id: 'form.abc.selectBehavior' })}</InputLabel>
                  <Select
                    value={newRecord.behavior}
                    label={intl.formatMessage({ id: 'form.abc.selectBehavior' })}
                    onChange={(e) => setNewRecord({ ...newRecord, behavior: e.target.value })}
                  >
                    {commonBehaviors.map((beh) => (
                      <MenuItem key={beh} value={beh}>
                        {beh}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  fullWidth
                  label={intl.formatMessage({ id: 'form.abc.durationSeconds' })}
                  type="number"
                  value={newRecord.duration}
                  onChange={(e) => setNewRecord({ ...newRecord, duration: e.target.value })}
                  sx={{ mb: 1 }}
                />
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  placeholder={intl.formatMessage({ id: 'form.abc.behaviorDetailsPlaceholder' })}
                  value={newRecord.behaviorDetails}
                  onChange={(e) => setNewRecord({ ...newRecord, behaviorDetails: e.target.value })}
                />
              </Box>

              {/* Consequence */}
              <Box>
                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1, color: 'success.main' }}>
                  {intl.formatMessage({ id: 'form.abc.consequenceHeader' })}
                </Typography>
                <FormControl fullWidth sx={{ mb: 1 }}>
                  <InputLabel>{intl.formatMessage({ id: 'form.abc.selectConsequence' })}</InputLabel>
                  <Select
                    value={newRecord.consequence}
                    label={intl.formatMessage({ id: 'form.abc.selectConsequence' })}
                    onChange={(e) => setNewRecord({ ...newRecord, consequence: e.target.value })}
                  >
                    {commonConsequences.map((con) => (
                      <MenuItem key={con} value={con}>
                        {con}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  placeholder={intl.formatMessage({ id: 'form.abc.consequenceDetailsPlaceholder' })}
                  value={newRecord.consequenceDetails}
                  onChange={(e) => setNewRecord({ ...newRecord, consequenceDetails: e.target.value })}
                />
              </Box>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancelForm}>{intl.formatMessage({ id: 'common.cancel' })}</Button>
            <Button
              variant="contained"
              startIcon={<CheckOutlined />}
              onClick={handleAddRecord}
              disabled={!newRecord.antecedent || !newRecord.behavior || !newRecord.consequence}
            >
              {editingRecord ? intl.formatMessage({ id: 'form.abc.update' }) : intl.formatMessage({ id: 'form.abc.addRecord' })}
            </Button>
          </DialogActions>
        </Dialog>
      </Stack>
    </LocalizationProvider>
  );
};

ABCMode.propTypes = {
  viewMode: PropTypes.string,
  onDataChange: PropTypes.func,
  // Shared ABC records state
  abcRecords: PropTypes.array,
  onRecordsChange: PropTypes.func,
  // Offline mode props
  isOfflineMode: PropTypes.bool,
  childId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  childName: PropTypes.string,
  therapistId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  therapistName: PropTypes.string,
  onSave: PropTypes.func,
  onBack: PropTypes.func
};

export default ABCMode;
